import React, { useState, useEffect, useMemo } from 'react';
import { PlayIcon, PauseIcon, ArrowPathIcon, MagnifyingGlassIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { Lead } from '../types';
import { LeadsAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';

type SortField = 'last_interaction' | 'name' | 'status';
type SortDir = 'asc' | 'desc';

const LeadsDashboard = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('last_interaction');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [botGlobalStatus, setBotGlobalStatus] = useState<'active' | 'paused'>('active');
  const [isBotToggling, setIsBotToggling] = useState(false);

  useEffect(() => {
    loadLeads();
    loadBotGlobalStatus();
  }, []);

  const loadBotGlobalStatus = async () => {
    const status = await LeadsAPI.getBotGlobalStatus();
    setBotGlobalStatus(status);
  };

  const handleToggleBotGlobal = async () => {
    setIsBotToggling(true);
    try {
      if (botGlobalStatus === 'active') {
        await LeadsAPI.pauseBotGlobal();
        setBotGlobalStatus('paused');
        toast.success('Bot pausado para todos os leads');
      } else {
        await LeadsAPI.resumeBotGlobal();
        setBotGlobalStatus('active');
        toast.success('Bot retomado para todos os leads');
      }
    } catch (error) {
      toast.error('Erro ao alterar status global do bot');
    } finally {
      setIsBotToggling(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronUpIcon className="h-3 w-3 text-gray-300 ml-1 inline" />;
    return sortDir === 'asc'
      ? <ChevronUpIcon className="h-3 w-3 text-indigo-500 ml-1 inline" />
      : <ChevronDownIcon className="h-3 w-3 text-indigo-500 ml-1 inline" />;
  };

  const filteredLeads = useMemo(() => {
    let result = [...leads];

    // Filtro de busca
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(l =>
        l.phone.includes(q) || (l.name || '').toLowerCase().includes(q)
      );
    }

    // Filtro de status
    if (filterStatus !== 'all') {
      result = result.filter(l => l.status === filterStatus);
    }

    // Ordenação
    result.sort((a, b) => {
      let va: any, vb: any;
      if (sortField === 'last_interaction') {
        va = a.last_interaction ? new Date(a.last_interaction).getTime() : 0;
        vb = b.last_interaction ? new Date(b.last_interaction).getTime() : 0;
      } else if (sortField === 'name') {
        va = (a.name || a.phone).toLowerCase();
        vb = (b.name || b.phone).toLowerCase();
      } else {
        va = a.status || '';
        vb = b.status || '';
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [leads, search, filterStatus, sortField, sortDir]);

  const loadLeads = async () => {
    try {
      setIsLoading(true);
      const leadsList = await LeadsAPI.listLeads();
      setLeads(leadsList);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao carregar leads');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePause = async (phone: string) => {
    try {
      await LeadsAPI.pauseLead(phone);
      toast.success(`Bot pausado para ${phone}`);
      loadLeads();
    } catch (error) {
      toast.error('Erro ao pausar bot');
    }
  };

  const handleResume = async (phone: string) => {
    try {
        await LeadsAPI.resumeLead(phone);
        toast.success(`Bot retomado para ${phone}`);
        loadLeads();
    } catch (error) {
        toast.error('Erro ao retomar bot');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gerenciamento de Leads</h1>
          <p className="mt-2 text-sm text-gray-600">
            Visualize o status de cada lead e controle manualmente a automação.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleToggleBotGlobal}
            disabled={isBotToggling}
            className={`inline-flex items-center px-4 py-2 rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors
              ${botGlobalStatus === 'active'
                ? 'border border-red-300 text-red-700 bg-white hover:bg-red-50 focus:ring-red-500'
                : 'border border-green-300 text-green-700 bg-white hover:bg-green-50 focus:ring-green-500'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            title={botGlobalStatus === 'active' ? 'Pausar bot para todos os leads' : 'Retomar bot para todos os leads'}
          >
            {botGlobalStatus === 'active' ? (
              <>
                <PauseIcon className="h-5 w-5 mr-2" />
                Pausar Bot
              </>
            ) : (
              <>
                <PlayIcon className="h-5 w-5 mr-2" />
                Retomar Bot
              </>
            )}
          </button>
          {botGlobalStatus === 'paused' && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              Bot Pausado
            </span>
          )}
          <button
            onClick={loadLeads}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <ArrowPathIcon className="h-5 w-5 mr-2 text-gray-500" />
            Atualizar
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome ou telefone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
        >
          <option value="all">Todos os status</option>
          <option value="active">Ativo</option>
          <option value="paused">Pausado</option>
          <option value="human">Humano</option>
          <option value="new">Novo</option>
          <option value="finished - vendas">Finished</option>
        </select>
        <span className="text-sm text-gray-500 self-center whitespace-nowrap">
          {filteredLeads.length} de {leads.length} leads
        </span>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="flex flex-col">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        onClick={() => handleSort('name')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      >
                        Lead <SortIcon field="name" />
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort('status')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      >
                        Status <SortIcon field="status" />
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Etapa Atual
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort('last_interaction')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      >
                        Última Interação <SortIcon field="last_interaction" />
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Ações</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredLeads.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-400">
                          Nenhum lead encontrado com os filtros aplicados.
                        </td>
                      </tr>
                    ) : filteredLeads.map((lead) => (
                      <tr key={lead.phone}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                              <div className="text-sm text-gray-500">{lead.phone}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${lead.status === 'active' ? 'bg-green-100 text-green-800' : 
                              lead.status === 'paused' ? 'bg-red-100 text-red-800' : 
                              lead.status === 'human' ? 'bg-yellow-100 text-yellow-800' : 
                              'bg-gray-100 text-gray-800'}`}>
                            {lead.status === 'active' ? 'Ativo' : 
                             lead.status === 'paused' ? 'Pausado' : 
                             lead.status === 'human' ? 'Humano' : lead.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {lead.current_flow}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {lead.last_interaction ? new Date(lead.last_interaction).toLocaleString('pt-BR') : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {lead.status === 'active' ? (
                            <button
                                onClick={() => handlePause(lead.phone)}
                                className="text-red-600 hover:text-red-900 inline-flex items-center"
                                title="Pausar Bot"
                            >
                                <PauseIcon className="h-5 w-5 mr-1" />
                                Pausar
                            </button>
                          ) : (
                            <button
                                onClick={() => handleResume(lead.phone)}
                                className="text-green-600 hover:text-green-900 inline-flex items-center"
                                title="Retomar Bot"
                            >
                                <PlayIcon className="h-5 w-5 mr-1" />
                                Retomar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadsDashboard;
